<template>
  <div
    class="carousel-playlist"
    :class="{
      isVertical,
      isBasicBook,
      isRankedCourse,
      isKidsVideoBook,
    }"
  >
    <swiper
      :simulate-touch="isTouchDevice"
      :modules="[Mousewheel, Navigation]"
      :navigation="true"
      :mousewheel="{ forceToAxis: true }"
      :space-between="24"
      :slidesPerGroup="1"
      :breakpoints="{
        0: {
          slidesPerView: 1.2,
          slidesOffsetBefore: 16,
          slidesOffsetAfter: 24,
        },
        300: {
          slidesPerView: elementsMobile,
          slidesOffsetAfter: 24,
          slidesOffsetBefore: 16,
        },
        576: {
          slidesPerView: elementsMobile,
          slidesOffsetBefore:
            slidesOffsetBeforeMobile ?? slidesOffsetBeforeDesktop * 0.5,
        },
        992: {
          slidesPerView: elementsDesktop,
          slidesOffsetBefore: slidesOffsetBeforeDesktop,
          slidesOffsetAfter:
            slidesOffsetAfterDesktop ?? slidesOffsetBeforeDesktop * 0.7,
          spaceBetween: 16,
        },
      }"
    >
      <swiper-slide v-for="(item, index) in items" :key="item.id">
        <div v-if="mastersAvatars">
          <teacher-icon :teacher="item" />
        </div>
        <div v-else-if="isKidsVideoBook">
          <video-book-item :item="item" class="w-100" />
        </div>
        <div v-else-if="isBasicBook">
          <library-item
            :hidde-locked="hiddeLocked"
            :item="item"
            class="w-100"
          />
          <show-authors
            v-if="showAuthors"
            class="infos mt-1"
            :items="item.authors"
            :class="{ 'text-center': authCenter }"
          />
        </div>
        <div v-else-if="isRankedCourse">
          <ranked-course :course="item" :index="index" />
        </div>
        <div v-else>
          <course-item
            :item="item"
            :shape="`${isVertical ? 'vertical' : 'horizontal'}`"
            :show-infos="showInfos"
            :show-progress="showProgress"
            :is-conference="isConference"
            :is-lesson="isLesson"
            :show-know-more="showKnowMore"
            :show-name="showName"
            :show-date="showDate"
            :noHover="noHover"
            :show-title-upper="showTitleUpper"
            :isHome="isHome"
            :show-authors="showAuthors"
            :isKeepWatching="isKeepWatching"
          >
            <show-authors
              v-if="showAuthors"
              :items="!item.teachers ? item.course.teachers : item.teachers"
            />
          </course-item>
        </div>
      </swiper-slide>
    </swiper>
  </div>
</template>
<script>
  import "swiper/css";
  import "swiper/css/mousewheel";
  import "swiper/css/navigation";

  import { Mousewheel, Navigation } from "swiper";
  import { Swiper, SwiperSlide } from "swiper/vue";

  import TeacherIcon from "@/components/TeacherIcon.vue";
  import VideoBookItem from "@/components/thumbnails/VideoBookItem.vue";
  import ShowAuthors from "@/components/ShowAuthors.vue";
  import CourseItem from "@/components/thumbnails/CourseItem";
  import LibraryItem from "@/components/thumbnails/LibraryItem.vue";
  import RankedCourse from "@/components/thumbnails/RankedCourse.vue";
  import isConference from "@/functions/mixins/isConference.js";

  export default {
    mixins: [isConference],
    components: {
      Swiper,
      SwiperSlide,
      TeacherIcon,
      VideoBookItem,
      ShowAuthors,
      CourseItem,
      LibraryItem,
      RankedCourse,
    },
    props: {
      isHome: {
        type: Boolean,
        default: false,
      },
      noHover: {
        type: Boolean,
        default: false,
      },
      items: {
        type: Array,
        default: () => [],
      },
      shape: {
        type: String,
        required: false,
      },
      isVertical: {
        type: Boolean,
        default: false,
      },
      isKidsVideoBook: {
        type: Boolean,
        default: false,
      },
      mastersAvatars: {
        type: Boolean,
        default: false,
      },
      showTitleUpper: {
        type: Boolean,
        default: false,
      },
      isBasicBook: {
        type: Boolean,
        default: false,
      },
      hiddeLocked: {
        type: Boolean,
        required: false,
        default: false,
      },
      authCenter: {
        type: Boolean,
        default: false,
      },
      isRankedCourse: {
        type: Boolean,
        default: false,
      },
      isLesson: {
        type: Boolean,
        default: false,
      },
      slidesOffsetBeforeDesktop: {
        required: false,
        default: 48,
        type: Number,
      },
      slidesOffsetAfterDesktop: {
        required: false,
        type: Number,
      },
      slidesOffsetBeforeMobile: {
        required: false,
        default: 24,
        type: Number,
      },
      showInfos: {
        type: Boolean,
        default: true,
      },
      showKnowMore: {
        type: Boolean,
        default: true,
      },
      showProgress: {
        type: Boolean,
        default: true,
      },
      showDate: {
        type: Boolean,
        default: false,
      },
      showName: {
        type: Boolean,
        default: false,
      },
      showAuthors: {
        type: Boolean,
        default: true,
      },
      elementsDesktop: {
        required: false,
        default: 4,
        type: Number,
      },
      elementsMobile: {
        required: false,
        default: 1.5,
        type: Number,
      },
      isKeepWatching: {
        type: Boolean,
        default: false,
      },
    },
    data() {
      return {
        Mousewheel,
        Navigation,
        isTouchDevice: false,
      };
    },
    mounted() {
      this.detectTouchDevice();
    },
    methods: {
      detectTouchDevice() {
        const userAgent = navigator.userAgent.toLowerCase();
        this.isTouchDevice = /mobile|android|touch|ipad|tablet/i.test(
          userAgent
        );
      },
    },
  };
</script>
<style lang="scss" scoped>
  .carousel-playlist {
    max-width: 100%;
    overflow-x: clip;

    .swiper-slide {
      cursor: pointer;

      &:hover {
        z-index: 5;
      }
    }

    .swiper {
      overflow: visible;

      @media (max-width: 768px) {
        max-height: 360px;
      }

      @media (min-width: 992px) {
        max-height: 220px;
      }

      @media (min-width: 1900px) {
        max-height: 290px;
      }

      &:hover {
        z-index: 2;
        .swiper-wrapper {
          overflow: visible;
          max-height: 169px;
        }
      }
    }

    &.isBasicBook,
    &.isRankedCourse {
      .swiper {
        max-height: fit-content;
      }
    }
    &.isVertical {
      .swiper {
        min-height: 430px;
        max-height: 700px;

        &:hover {
          .swiper-wrapper {
            max-height: 700px;
            min-height: 430px;
          }
        }

        @media (min-width: 1500px) {
          min-height: 480px;

          &:hover {
            .swiper-wrapper {
              max-height: 700px;
              min-height: 480px;
            }
          }
        }

        @media (min-width: 1700px) {
          min-height: 510px;

          &:hover {
            .swiper-wrapper {
              max-height: 700px;
              min-height: 510px;
            }
          }
        }

        @media (min-width: 1800px) {
          min-height: 545px;

          &:hover {
            .swiper-wrapper {
              max-height: 700px;
              min-height: 545px;
            }
          }
        }

        @media (max-width: 768px) {
          min-height: 300px;
          max-height: 700px;

          &:hover {
            .swiper-wrapper {
              max-height: 700px;
              min-height: 300px;
            }
          }
        }
        @media (min-width: 1900px) {
          min-height: 605px;

          &:hover {
            .swiper-wrapper {
              min-height: 605px;
            }
          }
        }
      }
    }

    :deep(.swiper-button-next) {
      z-index: 2;
      top: 20px;
      right: 0;
      height: 120%;
      // height: 68%;
      // margin-top: 0;
      padding-left: 30px;
      padding-right: 30px;

      font-weight: 900;
      background: linear-gradient(
        270deg,
        #000000 -29.99%,
        rgba(21, 32, 30, 0) 100%
      );
      font-size: 30px;
      transition: 0.2s cubic-bezier(0.5, 0, 0.1, 1) 0s;
    }

    :deep(.swiper-button-prev) {
      z-index: 2;
      top: 20px;
      left: 0;
      height: 120%;
      // margin-top: 0;
      padding-left: 30px;
      padding-right: 30px;
      // color: #fff0;
      font-weight: 900;
      background: linear-gradient(
        90deg,
        rgba(17, 17, 15, 0.9) 0%,
        rgba(17, 17, 15, 0.6) 55.73%,
        rgba(17, 17, 15, 0) 100%
      );
      font-size: 30px;
      transition: 0.2s cubic-bezier(0.5, 0, 0.1, 1) 0s;
    }

    &.isKidsVideoBook {
      .swiper {
        max-height: 420px;
      }
      :deep(.swiper-button-next) {
        height: 100%;
      }

      :deep(.swiper-button-prev) {
        height: 100%;
      }
    }
  }
</style>
